// Choose the Wi-Fi network to hand a device.
//
// Ports Flutter's wifiHandler.dart showWifiDialog, which lists the networks in
// range and asks for a password on the one you tap — plus the thing that dialog
// lacked: the networks ALREADY IN USE at this location come with their
// passwords, so the common case (a second network at a site you have already
// set up) is one tap and no typing.
//
// Three sources, in the order they save the most work:
//   1. Saved for this location — ssid AND password known, tap and send.
//   2. In range — scanned by the phone; tap, then type the password.
//   3. Other network — type both, for a network that is not broadcasting or is
//      out of range from where you are standing.
//
// The dialog only CHOOSES credentials. It does not send them: an installed
// device is given a network over MQTT, while a device still in setup mode is
// given one over its own hotspot, and those are the caller's business.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  List, ListItemButton, ListItemText, ListItemIcon, CircularProgress, TextField,
  InputAdornment, IconButton, Chip, Divider, useTheme,
} from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AddIcon from '@mui/icons-material/Add';
import {
  scanNetworks, wifiScanSupported, partitionNetworks,
  WIFI_OFF, PERMISSION_DENIED, PLUGIN_MISSING,
} from '../../../core/wifi/wifiScan';
import { getKnownNetworks, mergeScanWithKnown } from '../../../core/wifi/knownNetworks';

export default function WifiPickerDialog({
  open, locationId, deviceName, submitLabel = 'Send Wi-Fi', onClose, onSubmit,
}) {
  const theme = useTheme();
  const c = theme.sli;

  const [known, setKnown] = useState(null);       // [{ssid, pass, from}]
  const [serverFailed, setServerFailed] = useState(false);
  const [scanned, setScanned] = useState(null);   // [{ssid, band, level}]
  const [scanError, setScanError] = useState('');
  const [scanning, setScanning] = useState(false);

  // 'list' -> pick; 'entry' -> confirm/enter the password for one network
  const [mode, setMode] = useState('list');
  const [ssid, setSsid] = useState('');
  const [pass, setPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [ssidEditable, setSsidEditable] = useState(false);

  const doScan = useCallback(async () => {
    if (!wifiScanSupported()) {
      setScanned([]);
      setScanError('Scanning for networks needs the installed app.');
      return;
    }
    setScanning(true);
    setScanError('');
    try {
      const list = await scanNetworks();
      setScanned(partitionNetworks(list).usable);
    } catch (e) {
      setScanned([]);
      // These two are worth naming: the user can fix them, and "no networks
      // found" would send them looking in the wrong place entirely.
      setScanError(
        e.code === WIFI_OFF ? 'Turn Wi-Fi on to see the networks in range.'
          : e.code === PERMISSION_DENIED
            ? 'Permission is needed to list nearby networks. You can still add one by name.'
            : e.code === PLUGIN_MISSING
              // Actionable, and true: the feature arrives with an app update,
              // not by fiddling with the phone.
              ? 'Update the app from the Play Store to list nearby networks. '
                + 'Saved networks and adding one by name work as normal.'
              : 'Could not list nearby networks. You can still add one by name.',
      );
    } finally {
      setScanning(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setMode('list');
    setSsid(''); setPass(''); setShowPass(false);
    setKnown(null); setScanned(null); setScanError(''); setServerFailed(false);
    doScan();
    getKnownNetworks(locationId).then((r) => {
      setKnown(r.networks);
      setServerFailed(r.serverFailed);
    }).catch(() => setKnown([]));
  }, [open, locationId, doScan]);

  // Tapping a network goes to the same one-field step either way; the
  // difference is whether the password is already filled in.
  const choose = (name, password, editable = false) => {
    setSsid(name);
    setPass(password || '');
    setSsidEditable(editable);
    setShowPass(false);
    setMode('entry');
  };

  // ONE list: what the phone can see, merged with what we hold a password for.
  // Two separate lists made the same network appear twice — once tappable with
  // its password and once without — and there was no way to tell which was
  // which.
  const merged = useMemo(
    () => mergeScanWithKnown(scanned || [], known || []),
    [scanned, known],
  );

  const submit = () => {
    const s = ssid.trim();
    if (!s) return;
    onSubmit({ ssid: s, pass });
  };

  const paper = { bgcolor: c.back, borderRadius: '20px', width: 'min(92vw, 460px)' };
  const fieldSx = {
    my: 1,
    '& .MuiInputLabel-root': { color: `${c.text}B3` },
    '& .MuiOutlinedInput-root': {
      color: c.text,
      borderRadius: '12px',
      '& fieldset': { borderColor: `${c.text}66` },
      '&:hover fieldset': { borderColor: c.text },
      '&.Mui-focused fieldset': { borderColor: c.text },
    },
  };

  const sectionTitle = (text, action) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
      <Typography sx={{ color: c.text, fontWeight: 700, fontSize: 14 }}>{text}</Typography>
      {action}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: paper }}>
      <DialogTitle sx={{ color: c.text, fontWeight: 700, fontSize: 18, pb: 0.5 }}>
        {mode === 'entry' ? 'Wi-Fi Password' : 'Choose Wi-Fi'}
        {deviceName && (
          <Typography sx={{ color: `${c.text}B3`, fontSize: 13, fontWeight: 400 }}>
            {deviceName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {mode === 'entry' ? (
          <>
            <TextField
              fullWidth
              label="Wi-Fi Name (SSID)"
              value={ssid}
              onChange={(e) => setSsid(e.target.value)}
              disabled={!ssidEditable}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              autoFocus={!pass}
              label="Wi-Fi Password"
              type={showPass ? 'text' : 'password'}
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              sx={fieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPass((v) => !v)} sx={{ color: c.text }}>
                      {showPass ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography sx={{ color: `${c.text}99`, fontSize: 12, mt: 1 }}>
              Sent exactly as typed — the SSID and password are case-sensitive.
              These devices join 2.4 GHz networks only.
            </Typography>
          </>
        ) : (
          <>
            {/* ONE list. A network that is both in range and already known
                appears once, with its password — not twice, once tappable and
                once not. */}
            {sectionTitle('Networks', (
              <IconButton onClick={doScan} disabled={scanning} size="small" sx={{ color: c.text }}>
                {scanning ? <CircularProgress size={16} sx={{ color: c.text }} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            ))}

            {known === null ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <CircularProgress size={16} sx={{ color: c.text }} />
                <Typography sx={{ color: `${c.text}B3`, fontSize: 13 }}>
                  Looking for networks…
                </Typography>
              </Box>
            ) : merged.length === 0 ? (
              <Typography sx={{ color: `${c.text}99`, fontSize: 13, py: 0.5 }}>
                {serverFailed
                  ? 'The saved list could not be loaded and nothing is in range.'
                  : 'Nothing in range, and nothing saved for this location yet.'}
              </Typography>
            ) : (
              <List dense disablePadding sx={{ maxHeight: 260, overflowY: 'auto' }}>
                {merged.map((n) => (
                  <ListItemButton
                    key={n.ssid}
                    onClick={() => choose(n.ssid, n.pass)}
                    sx={{ borderRadius: '12px' }}
                  >
                    <ListItemIcon sx={{ color: c.text, minWidth: 34 }}>
                      {n.secured === false ? <WifiIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={n.ssid}
                      secondary={n.known
                        ? `Saved${n.inRange ? '' : ' · not in range here'}`
                        : (n.inRange ? 'Password needed' : '')}
                      primaryTypographyProps={{ sx: { color: c.text, fontWeight: 600 } }}
                      secondaryTypographyProps={{ sx: { color: `${c.text}99`, fontSize: 12 } }}
                    />
                    {/* The devices are 2.4 GHz only. Handing one a 5 GHz SSID
                        fails silently and looks like dead hardware. */}
                    {n.inRange && n.band && (
                      <Chip
                        size="small"
                        label={`${n.band} GHz`}
                        color={n.band === '5' ? 'error' : 'success'}
                        sx={{ height: 18, fontSize: 10, mr: 0.5 }}
                      />
                    )}
                    {n.known && (
                      <Chip
                        size="small" label="saved" variant="outlined"
                        sx={{ height: 18, fontSize: 10, color: c.text, borderColor: `${c.text}66` }}
                      />
                    )}
                  </ListItemButton>
                ))}
              </List>
            )}

            {scanError && (
              <Typography sx={{ color: `${c.text}99`, fontSize: 12, py: 0.5 }}>{scanError}</Typography>
            )}

            <Divider sx={{ borderColor: `${c.text}33`, mt: 1.5 }} />

            {/* 3. Anything else. */}
            <Button
              startIcon={<AddIcon />}
              onClick={() => choose('', '', true)}
              sx={{ mt: 1, color: c.text, textTransform: 'none', fontWeight: 700 }}
            >
              Other network…
            </Button>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {mode === 'entry' && (
          <Button onClick={() => setMode('list')} sx={{ color: c.text }}>Back</Button>
        )}
        <Button onClick={onClose} sx={{ color: c.text }}>Cancel</Button>
        {mode === 'entry' && (
          <Button
            variant="contained"
            onClick={submit}
            disabled={!ssid.trim()}
            sx={{ bgcolor: c.button, color: '#fff', borderRadius: '12px' }}
          >
            {submitLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
